import React, { useState } from 'react';
import { X, TrendingUp, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';

export default function LoginPage({ asModal = false, onClose }) {
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [step, setStep] = useState(1); // 1 = credentials, 2 = preferences (signup only)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [signupBankroll, setSignupBankroll] = useState('1000');
  const [signupBooks, setSignupBooks] = useState([]);
  const [signupAlertEnabled, setSignupAlertEnabled] = useState(false);
  const [signupAlertThreshold, setSignupAlertThreshold] = useState('1.0');
  const [signupAlertAllBooks, setSignupAlertAllBooks] = useState(false);
  const [error, setError] = useState(null);
  const [signupPending, setSignupPending] = useState(false);
  const [forgotPending, setForgotPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleBook = (key) => {
    setSignupBooks(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Step 1 in signup mode: validate passwords match then advance to step 2
    if (mode === 'signup' && step === 1) {
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      setStep(2);
      return;
    }

    setLoading(true);

    const metadata = {
      first_name: firstName,
      last_name: lastName,
      phone,
      ...(signupBankroll ? { preferred_bankroll: Number(signupBankroll) } : {}),
      ...(signupBooks.length > 0 ? { preferred_books: signupBooks } : {}),
      arb_alert_enabled: signupAlertEnabled,
      arb_alert_threshold: parseFloat(signupAlertThreshold) || 1.0,
      arb_alert_all_books: signupAlertAllBooks,
    };

    const { error } = mode === 'signup'
      ? await signUp(email, password, metadata)
      : await signIn(email, password);

    setLoading(false);

    if (error) {
      setError(error.message);
    } else if (mode === 'signup') {
      setSignupPending(true);
    }
  };

  const handleGoogle = async (credential) => {
    setError(null);
    const { error } = await signInWithGoogle(credential);
    if (error) setError(error.message);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) setError(error.message);
    else setForgotPending(true);
  };

  const toggleMode = () => {
    setMode(m => m === 'signin' ? 'signup' : 'signin');
    setStep(1);
    setError(null);
    setForgotPending(false);
    setFirstName('');
    setLastName('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setSignupBankroll('1000');
    setSignupBooks([]);
    setSignupAlertEnabled(false);
    setSignupAlertThreshold('1.0');
    setSignupAlertAllBooks(false);
  };

  const isStep2 = mode === 'signup' && step === 2;

  if (signupPending) {
    const pendingCard = (
      <div className={asModal ? 'login-modal-card' : 'login-card'}>
        {asModal && (
          <button className="login-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        )}
        <div className="login-brand">
          <div className="login-brand-icon"><TrendingUp size={20} /></div>
          <h1 className="login-title">Check your email</h1>
        </div>
        <div className="signup-pending">
          <p className="signup-pending-body">
            If <strong>{email}</strong> isn't already registered, you'll receive a confirmation link shortly. Click it and you'll be signed in automatically.
          </p>
          <p className="signup-pending-note">Don't see it? Check your spam folder. If the email is already in use, try signing in instead.</p>
        </div>
        <p className="login-toggle">
          Wrong email?{' '}
          <button className="login-toggle-btn" onClick={() => { setSignupPending(false); setStep(1); }}>
            Go back
          </button>
        </p>
      </div>
    );
    if (asModal) {
      return (
        <div className="login-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
          {pendingCard}
        </div>
      );
    }
    return <div className="login-page">{pendingCard}</div>;
  }

  if (mode === 'forgot') {
    const forgotCard = (
      <div className={asModal ? 'login-modal-card' : 'login-card'}>
        {asModal && (
          <button className="login-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        )}
        <div className="login-brand">
          <div className="login-brand-icon"><TrendingUp size={20} /></div>
          <h1 className="login-title">Reset password</h1>
        </div>
        {forgotPending ? (
          <>
            <div className="signup-pending">
              <p className="signup-pending-body">
                We sent a password reset link to <strong>{email}</strong>. Click it to set a new password.
              </p>
              <p className="signup-pending-note">Don't see it? Check your spam folder.</p>
            </div>
            <p className="login-toggle">
              <button className="login-toggle-btn" onClick={() => { setMode('signin'); setForgotPending(false); }}>
                Back to sign in
              </button>
            </p>
          </>
        ) : (
          <>
            <p className="login-subtitle">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={handleForgot} className="login-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="login-input"
              />
              {error && <p className="login-error">{error}</p>}
              <button type="submit" className="login-submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
            <p className="login-toggle">
              <button className="login-toggle-btn" onClick={() => setMode('signin')}>
                Back to sign in
              </button>
            </p>
          </>
        )}
      </div>
    );
    if (asModal) {
      return (
        <div className="login-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
          {forgotCard}
        </div>
      );
    }
    return <div className="login-page">{forgotCard}</div>;
  }

  const cardContent = (
    <div className={asModal ? 'login-modal-card' : 'login-card'}>
      {asModal && (
        <button className="login-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      )}

      <div className="login-brand">
        <div className="login-brand-icon"><TrendingUp size={20} /></div>
        <h1 className="login-title">NavBets</h1>
      </div>

      <p className="login-subtitle">
        {mode === 'signin' ? 'Sign in to your account' : isStep2 ? 'Set up your account' : 'Create an account'}
      </p>

      {!isStep2 && (
        <>
          <div className="login-google-wrapper">
            <GoogleLogin
              onSuccess={({ credential }) => handleGoogle(credential)}
              onError={() => setError('Google sign-in failed. Please try again.')}
              useOneTap={false}
              width="360"
              text="continue_with"
            />
          </div>
          <div className="login-divider"><span>or</span></div>
        </>
      )}

      <form onSubmit={handleSubmit} className="login-form">
        {/* Step 1: personal info */}
        {!isStep2 && (
          <>
            {mode === 'signup' && (
              <>
                <div className="login-name-row">
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    className="login-input"
                  />
                  <input
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    className="login-input"
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  className="login-input"
                />
              </>
            )}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="login-input"
            />
            <div className="login-password-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="login-input"
              />
              <button type="button" className="login-password-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {mode === 'signup' && (
              <div className="login-password-wrap">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  className="login-input"
                />
                <button type="button" className="login-password-toggle" onClick={() => setShowConfirmPassword(v => !v)} tabIndex={-1}>
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            )}
            {mode === 'signin' && (
              <button type="button" className="login-forgot-btn" onClick={() => { setMode('forgot'); setError(null); }}>
                Forgot password?
              </button>
            )}
          </>
        )}

        {/* Step 2: preferences (optional) */}
        {isStep2 && (
          <>
            <p className="signup-optional-note">These are optional — you can change them anytime in Settings.</p>

            <div className="signup-bankroll-section">
              <label className="signup-section-label">
                Starting bankroll
                <span className="signup-info-tip">
                  i
                  <span className="signup-info-tooltip">Your total betting budget. NavBets uses this to calculate how much to recommend betting on each opportunity.</span>
                </span>
              </label>
              <div className="signup-bankroll-input-row">
                <span className="signup-bankroll-symbol">$</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={signupBankroll}
                  onChange={e => setSignupBankroll(e.target.value)}
                  className="login-input signup-bankroll-input"
                />
              </div>
            </div>

            <div className="signup-books-section">
              <label className="signup-section-label">Which sportsbooks do you use?</label>
              <div className="signup-books-grid">
                {Object.entries(SPORTSBOOK_NAMES).map(([key, name]) => (
                  <button
                    key={key}
                    type="button"
                    className={`signup-book-pill${signupBooks.includes(key) ? ' selected' : ''}`}
                    onClick={() => toggleBook(key)}
                  >
                    <SportsbookLogo bookKey={key} size={18} />
                    {name}
                  </button>
                ))}
              </div>
            </div>
            <div className="signup-alert-section">
              <div className="settings-field settings-field--toggle">
                <label className="settings-label settings-label--toggle">
                  <span className="signup-section-label" style={{margin: 0}}>Arbitrage email alerts</span>
                  <button
                    type="button"
                    className={`toggle-btn${signupAlertEnabled ? ' toggle-btn--on' : ''}`}
                    onClick={() => setSignupAlertEnabled(v => !v)}
                    aria-pressed={signupAlertEnabled}
                  >
                    <span className="toggle-thumb" />
                  </button>
                </label>
              </div>
              <p className="signup-alert-note">Get emailed when new arb opportunities arise.</p>
              <p className="signup-alert-note" style={{marginTop: 2}}>Set a minimum profit % so you only get alerted for arbs above that return.</p>
              {signupAlertEnabled && (
                <div className="signup-alert-sub">
                  <label>
                    Min profit %:
                    <input
                      type="number"
                      value={signupAlertThreshold}
                      onChange={e => setSignupAlertThreshold(e.target.value)}
                      min="0.1"
                      step="0.1"
                    />
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={signupAlertAllBooks}
                      onChange={e => setSignupAlertAllBooks(e.target.checked)}
                    />
                    Include all books (not just my selection)
                  </label>
                </div>
              )}
            </div>
          </>
        )}

        {error && <p className="login-error">{error}</p>}

        {isStep2 ? (
          <div className="signup-step2-actions">
            <button type="button" className="signup-back-btn" onClick={() => setStep(1)}>
              ← Back
            </button>
            <button type="submit" className="login-submit-btn" disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>
        ) : (
          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Next →'}
          </button>
        )}
      </form>

      <p className="login-toggle">
        {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
        <button className="login-toggle-btn" onClick={toggleMode}>
          {mode === 'signin' ? 'Sign up' : 'Sign in'}
        </button>
      </p>
    </div>
  );

  if (asModal) {
    return (
      <div
        className="login-modal-overlay"
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div className="login-page">
      {cardContent}
    </div>
  );
}

